/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/no-unstable-nested-components */
import {
  documentToReactComponents,
  Options,
} from '@contentful/rich-text-react-renderer'
import {
  Block,
  BLOCKS,
  Inline,
  INLINES,
  Text,
} from '@contentful/rich-text-types'
import React from 'react'
import styled from 'styled-components'

import { NewsFieldsFragment } from '../../../generated/graphql'
import theme from '../../../theme'
import toDisplayDateText from '../../../utils/DateFormat'
import Media from '../../../utils/Media'
import { Container } from '../../shared/Container'
import Layout from '../../shared/Layout'
import PageHeader from '../../shared/PageHeader'
import Section from '../../shared/Section'

export type Entry = {
  id: string
  title: string
}

export type Asset = {
  id: string
  title: string
  url: string
}

interface StaticNewsProps {
  news: NewsFieldsFragment
  entries: [Entry]
  assets: [Asset]
}

const IndexTemplate: React.FC<StaticNewsProps> = ({
  news,
  entries,
  assets,
}) => {
  const options: Options = {
    renderText: (text) =>
      text.split('\n').reduce(
        (children: any[], textSegment: string, index: number) =>
          // eslint-disable-next-line react/no-array-index-key
          [...children, index > 0 && <br key={index} />, textSegment],
        []
      ),
    renderNode: {
      [INLINES.ENTRY_HYPERLINK]: (node: Block | Inline) => (
        <a href={node.data.target.sys.id}>{(node.content[0] as Text).value}</a>
      ),

      [INLINES.ASSET_HYPERLINK]: (node: Block | Inline) => {
        const asset = assets.filter(
          (assetData) => assetData.id === node.data.target.sys.id
        )

        if (asset == null) {
          return <></>
        }

        if (asset.length <= 0) {
          return <></>
        }

        return <a href={asset[0].url}>{(node.content[0] as Text).value}</a>
      },

      [INLINES.EMBEDDED_ENTRY]: (node: Block | Inline) => {
        const entry = entries.filter(
          (entryData) => entryData.id === node.data.target.sys.id
        )

        if (entry == null) {
          return <></>
        }

        if (entry.length <= 0) {
          return <></>
        }

        return (
          <StyledInlineEntryCard>
            <StyledInlineEntryCardStatus />
            <StyledInlineEntryCardText>
              {entry[0].title}
            </StyledInlineEntryCardText>
          </StyledInlineEntryCard>
        )
      },

      [BLOCKS.EMBEDDED_ENTRY]: (node: Block | Inline) => {
        const entry = entries.filter(
          (entryData) => entryData.id === node.data.target.sys.id
        )

        if (entry == null) {
          return <></>
        }

        if (entry.length <= 0) {
          return <></>
        }

        return (
          <StyledEntryCard>
            <StyledEntryCardTitle>News</StyledEntryCardTitle>
            <StyledEntryCardText>{entry[0].title}</StyledEntryCardText>
          </StyledEntryCard>
        )
      },

      [BLOCKS.EMBEDDED_ASSET]: (node: Block | Inline) => {
        const asset = assets.filter(
          (assetData) => assetData.id === node.data.target.sys.id
        )

        if (asset == null) {
          return <></>
        }

        if (asset.length <= 0) {
          return <></>
        }

        return (
          <p>
            <StyledImg src={asset[0].url} alt={asset[0].title} />
          </p>
        )
      },
    },
  }

  return (
    <Layout>
      <PageHeader
        label="お知らせ"
        backgroundImage="/images/picture/news_hero.jpg"
      />

      <StyledSection>
        <StyledContainer size="large">
          <div>
            <StyledDate>{toDisplayDateText(news.date)}</StyledDate>
            <StyledContentTitle>{news.title}</StyledContentTitle>
            <StyledContent>
              {documentToReactComponents(news.content?.json, options)}
            </StyledContent>
          </div>
        </StyledContainer>
      </StyledSection>
    </Layout>
  )
}

const StyledContainer = styled(Container)``

const StyledImg = styled.img`
  width: auto;
  max-width: 100%;
  max-height: 300px;
`

const StyledEntryCard = styled.span`
  display: block;
  box-sizing: border-box;
  height: 8.4375rem;
  margin: 0px 0px 1.25rem;
  padding: 0.875rem;
  overflow: auto;
  border: 1px solid #d3dce0;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  font-family:
    -apple-system,
    BlinkMacSystemFont,
    Segoe UI,
    Helvetica,
    Arial,
    sans-serif,
    Apple Color Emoji,
    Segoe UI Emoji,
    Segoe UI Symbol;
  font-size: 0.875rem;
  line-height: 1.5;
  text-transform: none;
`

const StyledEntryCardTitle = styled.span`
  display: block;
  margin-bottom: 0.25rem;
  padding-right: 1.25rem;
  color: #606c7c;
  font-size: 1rem;
  word-break: break-word;
`

const StyledEntryCardText = styled.span`
  margin: 0;
  overflow: hidden;
  color: #192532;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.5;
  text-overflow: ellipsis;
  word-break: break-word;
`

const StyledInlineEntryCard = styled.span`
  display: inline-flex;
  position: relative;
  align-items: center;
  max-width: 100%;
  padding: 0 0.25rem 0 0.75rem;
  transition: width 0.2s ease-in-out;
  border: 1px solid #d3dce0;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  color: inherit;
  font-family:
    -apple-system,
    BlinkMacSystemFont,
    Segoe UI,
    Helvetica,
    Arial,
    sans-serif,
    Apple Color Emoji,
    Segoe UI Emoji,
    Segoe UI Symbol;
  font-size: inherit;
  font-weight: inherit;
  line-height: 1.5;
  text-decoration: none;
`

const StyledInlineEntryCardStatus = styled.span`
  display: block;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 0.25rem;
  height: 100%;
  outline: 0.0625rem solid #1a593e;
  background: #16875d;
`

const StyledInlineEntryCardText = styled.span`
  max-height: 1.5em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StyledSection = styled(Section)`
  padding: 0 0 150px;

  ${Media.mobile} {
    padding: 30px 0 100px;
  }
`

const StyledDate = styled.p`
  font-size: 16px;
  line-height: 42px;

  ${Media.mobileTablet} {
    line-height: 20px;
  }
`

const StyledContentTitle = styled.h3`
  ${theme.typography.DefShueiMincho3}
`

// Contentfulで登録したHTMLタグなどをデザイン
const StyledContent = styled.div`
  margin: 10px 0 0;
  padding-top: 26px;
  border-top: 1px solid #000;
  font-size: 20px;
  line-height: 35px;

  ${theme.typography.DefNotoSansBody}

  ${Media.mobileTablet} {
    margin: 27px 0 0;
    font-size: 14px;
    line-height: 24.5px;
  }

  h1 {
    display: block;
    margin: 0px 0px 0.75rem;
    margin-block-start: 0.67em;
    margin-block-end: 0.67em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-size: 1.875rem;
    font-weight: 600;
    line-height: 1.3;
  }

  h2 {
    display: block;
    margin: 0px 0px 0.75rem;
    margin-block-start: 0.83em;
    margin-block-end: 0.83em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-size: 1.5625rem;
    font-weight: 600;
    line-height: 1.3;
  }

  h3 {
    display: block;
    margin: 0px 0px 0.75rem;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-size: 1.375rem;
    font-weight: 600;
    line-height: 1.3;
  }

  h4 {
    display: block;
    margin: 0px 0px 0.75rem;
    margin-block-start: 1.33em;
    margin-block-end: 1.33em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.3;
  }

  h5 {
    display: block;
    margin: 0px 0px 0.75rem;
    margin-block-start: 1.67em;
    margin-block-end: 1.67em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-size: 1.125rem;
    font-weight: 600;
    line-height: 1.3;
  }

  h6 {
    display: block;
    margin: 0px 0px 0.75rem;
    margin-block-start: 2.33em;
    margin-block-end: 2.33em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.3;
  }

  b {
    font-weight: 700;
  }

  i {
    font-style: italic;
    line-height: 1;
  }

  u {
    text-decoration: underline;
  }

  code {
    font-family: SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 0.9em;
  }

  a {
    color: #2d64b3;
    font-family:
      -apple-system,
      BlinkMacSystemFont,
      Segoe UI,
      Helvetica,
      Arial,
      sans-serif,
      Apple Color Emoji,
      Segoe UI Emoji,
      Segoe UI Symbol;
    font-size: inherit !important;
    text-decoration: underline;
    cursor: pointer;
  }

  a:hover {
    opacity: 0.7;
  }

  ul {
    margin: 0px 0px 1.25rem 1.25rem;
    list-style-type: disc;
    font-size: 16px;
    line-height: 1.5;
  }

  li {
    margin: 0px;
    list-style: inherit;

    P {
      margin: 0;
    }
  }

  ::marker {
    unicode-bidi: isolate;
    font-variant-numeric: tabular-nums;
    text-transform: none;
  }

  ul ul {
    margin-block-start: 0px;
    margin-block-end: 0px;
    list-style-type: circle;
  }

  ul ul ul {
    list-style-type: square;
  }

  ol {
    margin: 0px 0px 1.25rem 1.25rem;
    list-style-type: decimal;
    font-size: 16px;
    line-height: 1.5;
  }

  ol ol {
    margin-block-start: 0px;
    margin-block-end: 0px;
    list-style-type: upper-alpha;
  }

  ol ol ol {
    list-style-type: lower-roman;
  }

  ol ol ol ol {
    list-style-type: lower-alpha;
  }

  blockquote {
    margin: 0px 0px 1.3125rem;
    padding-left: 0.875rem;
    border-left: 6px solid rgb(229, 235, 237);
    font-style: normal;
    word-break: break-all;
  }

  hr {
    height: 1px;
    margin: 0px 0px 1.5rem;
    border: none;
    background-color: rgb(211, 220, 224);
    color: rgb(211, 220, 224);
  }
`

export default IndexTemplate
